import { useEffect, useState, type FormEvent } from 'react';
import { Building2, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { useClients } from '@/hooks/useClients';
import { useCreateOrder, useUpdateOrder, type SaveOrderInput } from '@/hooks/useSales';
import { useAuth } from '@/context/AuthContext';
import { can } from '@/types';
import { computeTotals, type LineItem, type Order } from '@/types/sales';
import { LineItemEditor } from '@/components/quotes/LineItemEditor';
import { TotalsPanel } from '@/components/quotes/TotalsPanel';

type Props = {
  open: boolean;
  onClose: () => void;
  order?: Order | null;
  defaultClientId?: string;
};

const today = () => new Date().toISOString().slice(0, 10);
const inDays = (n: number) => {
  const d = new Date(); d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

export function OrderFormModal({ open, onClose, order, defaultClientId }: Props) {
  const { user } = useAuth();
  const showCost = user ? can(user.role, 'financials', 'read') : false;

  const { data: clients } = useClients({ limit: 500 });
  const create = useCreateOrder();
  const update = useUpdateOrder();
  const editing = !!order;

  const [form, setForm] = useState<SaveOrderInput>({
    status: 'received',
    clientId: defaultClientId ?? '',
    projectName: '',
    poNumber: '',
    receivedDate: today(),
    promisedDate: inDays(45),
    lineItems: [],
    discountPct: 0,
    taxRatePct: 0.0975,
    shipping: 0,
    notes: '',
    internalNotes: '',
    shippingAddress: { country: 'United States' },
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (order) {
        setForm({
          status: order.status,
          clientId: order.clientId,
          contactId: order.contactId,
          projectName: order.projectName ?? '',
          poNumber: order.poNumber ?? '',
          sourceQuoteId: order.sourceQuoteId,
          receivedDate: order.receivedDate?.slice(0, 10) ?? today(),
          promisedDate: order.promisedDate?.slice(0, 10) ?? inDays(45),
          shippingAddress: order.shippingAddress ?? { country: 'United States' },
          lineItems: order.lineItems ?? [],
          discountPct: order.discountPct ?? 0,
          taxRatePct: order.taxRatePct ?? 0,
          shipping: order.shipping ?? 0,
          notes: order.notes ?? '',
          internalNotes: order.internalNotes ?? '',
          clientName: order.clientName,
        });
      } else {
        setForm((f) => ({
          ...f, clientId: defaultClientId ?? '', lineItems: [],
          receivedDate: today(), promisedDate: inDays(45),
        }));
      }
      setError(null);
    }
  }, [open, order, defaultClientId]);

  function update_<K extends keyof SaveOrderInput>(key: K, val: SaveOrderInput[K]) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  function updateAddr(key: keyof NonNullable<SaveOrderInput['shippingAddress']>, val: string) {
    setForm((f) => ({ ...f, shippingAddress: { ...f.shippingAddress, [key]: val } }));
  }

  function setLineItems(items: LineItem[]) {
    update_('lineItems', items);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.clientId) { setError('Please select a client.'); return; }
    if (form.lineItems.length === 0) { setError('Add at least one line item.'); return; }

    try {
      const totals = computeTotals(form.lineItems, {
        discountPct: form.discountPct,
        taxRatePct: form.taxRatePct,
        shipping: form.shipping,
      });
      const payload = { ...form, totals };

      if (editing && order) {
        await update.mutateAsync({ id: order.id, ...payload });
        toast.success(`Updated ${order.number}`);
      } else {
        const result = await create.mutateAsync(payload);
        toast.success(`Created ${result.number}`);
      }
      onClose();
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Save failed.');
    }
  }

  const submitting = create.isPending || update.isPending;
  const clientOpts = [
    { value: '', label: '— Select a client —' },
    ...(clients ?? []).map((c) => ({ value: c.id, label: c.name })),
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      kicker={editing ? 'Edit Order' : 'New Order'}
      title={editing ? `Edit ${order?.number}` : 'Create a work order.'}
      description={editing ? undefined : 'Enter a sales order from a PO. Will flow to the production floor once scheduled.'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button type="submit" form="order-form" loading={submitting}>
            {editing ? 'Save Changes' : 'Create Order'}
          </Button>
        </>
      }
    >
      <form id="order-form" onSubmit={onSubmit} className="space-y-6 py-2">
        {/* Header */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Client"
            value={form.clientId}
            onChange={(e) => update_('clientId', e.target.value)}
            options={clientOpts}
            required
          />
          <Input
            label="Project Name"
            placeholder="e.g. Downtown LA Lobby Refit"
            value={form.projectName ?? ''}
            onChange={(e) => update_('projectName', e.target.value)}
            leftIcon={<Building2 size={15} />}
          />
          <Input
            label="Client PO Number"
            placeholder="Optional"
            value={form.poNumber ?? ''}
            onChange={(e) => update_('poNumber', e.target.value)}
          />
          <Input
            label="Promised Delivery Date"
            type="date"
            value={form.promisedDate?.slice(0, 10) ?? ''}
            onChange={(e) => update_('promisedDate', e.target.value)}
          />
        </div>

        {/* Shipping address */}
        <div>
          <div className="kicker mb-3 flex items-center gap-2">
            <Truck size={11} /> Shipping Address
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Street 1"
              value={form.shippingAddress?.street1 ?? ''}
              onChange={(e) => updateAddr('street1', e.target.value)}
              className="md:col-span-2"
            />
            <Input label="City"        value={form.shippingAddress?.city ?? ''}        onChange={(e) => updateAddr('city', e.target.value)} />
            <Input label="State"       value={form.shippingAddress?.state ?? ''}       onChange={(e) => updateAddr('state', e.target.value)} />
            <Input label="Postal Code" value={form.shippingAddress?.postalCode ?? ''}  onChange={(e) => updateAddr('postalCode', e.target.value)} />
            <Input label="Country"     value={form.shippingAddress?.country ?? ''}     onChange={(e) => updateAddr('country', e.target.value)} />
            <Input label="Site Contact"       value={form.shippingAddress?.contactName ?? ''}  onChange={(e) => updateAddr('contactName', e.target.value)} />
            <Input label="Site Contact Phone" value={form.shippingAddress?.contactPhone ?? ''} onChange={(e) => updateAddr('contactPhone', e.target.value)} />
          </div>
        </div>

        {/* Line items */}
        <div>
          <div className="kicker mb-3">Line Items</div>
          <LineItemEditor
            items={form.lineItems}
            onChange={setLineItems}
            showCost={showCost}
          />
        </div>

        {/* Totals */}
        <TotalsPanel
          lineItems={form.lineItems}
          discountPct={form.discountPct}
          taxRatePct={form.taxRatePct}
          shipping={form.shipping}
          onDiscountChange={(v) => update_('discountPct', v)}
          onTaxRateChange={(v) => update_('taxRatePct', v)}
          onShippingChange={(v) => update_('shipping', v)}
          showMargin={showCost}
        />

        {/* Notes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Textarea
            label="Customer-facing Notes"
            rows={3}
            value={form.notes ?? ''}
            onChange={(e) => update_('notes', e.target.value)}
          />
          <Textarea
            label="Internal Notes"
            rows={3}
            placeholder="Production-only context."
            value={form.internalNotes ?? ''}
            onChange={(e) => update_('internalNotes', e.target.value)}
          />
        </div>

        {error && (
          <div className="text-xs text-danger border-l-2 border-danger pl-3 py-1">{error}</div>
        )}
      </form>
    </Modal>
  );
}
